package com.essntl.core.quickbooks.data

import com.essntl.config.EssntlConfig
import com.essntl.core.quickbooks.domain.QuickbooksClient
import com.essntl.core.utils.error.BadRequestException
import com.essntl.core.utils.io.saveToTempFile
import com.essntl.core.utils.repository.Repository
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import org.koin.core.annotation.Single

@Single(binds = [QuickbooksClient::class])
class QuickbooksClientImpl(
    private val client: HttpClient,
) : Repository(), QuickbooksClient {

    override val token = MutableStateFlow("")

    override var expiresAt = MutableStateFlow("")

    private fun isTokenExpired(): Boolean {
        if (token.value.isEmpty() || expiresAt.value.isEmpty())
            return true

        val tokenMillis =
            try {
                Instant.parse(expiresAt.value)
            } catch (e: Exception) {
                return true
            }

        val currentMillis = Clock.System.now()

        return (tokenMillis < currentMillis)
    }

    private suspend fun refreshToken() {
        val response = client.get("${EssntlConfig.quickbooksRefreshTokenUrl}/token") {
            contentType(ContentType.Application.Json)
        }

        val responseToken =
            try {
                response.body<Map<String, String>>()["access_token"]
            } catch (e: Exception) {
                null
            }

        val responseExpiresAt =
            try {
                response.body<Map<String, String>>()["expires_at"]
            } catch (e: Exception) {
                null
            }

        when {
            response.status.isSuccess() && responseToken != null && responseExpiresAt != null -> {
                token.value = responseToken
                expiresAt.value = responseExpiresAt
            }

            response.status.value == 401 ->
                throw BadRequestException(
                    message = "Unauthorized access to Quickbooks API",
                    code = 401,
                    cause = null,
                )

            else ->
                throw Exception("Failed to get access token")
        }
    }

    private suspend fun withTokenCheck(
        call: suspend () -> HttpResponse,
    ): HttpResponse {
        if (isTokenExpired())
            refreshToken()

        var response = call()

        if (response.status.value == 401) {
            refreshToken()
            response = call()
        }

        return response
    }

    override suspend fun getOrInsertCustomer(customerModel: QuickbooksCustomerRequest): Result<QuickbooksCustomer> {
        val getCustomerResult =
            runCatching {
                val customerExistsDto = getCustomerByEmail(email = customerModel.PrimaryEmailAddr!!.Address)

                customerExistsDto?.let {
                    return@runCatching QuickbooksCustomer.empty.copy(
                        Id = customerExistsDto.Id,
                        primaryEmailAddr = customerModel.PrimaryEmailAddr,
                        GivenName = customerModel.GivenName,
                        DisplayName = customerModel.DisplayName,
                        FullyQualifiedName = customerModel.DisplayName,
                    )
                }
            }.getOrNull()

        return if (getCustomerResult != null)
            Result.success(getCustomerResult)
        else
            insertCustomer(customerModel)
    }

    override suspend fun insertCustomer(customerModel: QuickbooksCustomerRequest): Result<QuickbooksCustomer> =
        runCatching {
            val response =
                withTokenCheck {
                    client.post("${EssntlConfig.quickbooksBaseUrl}/customer") {
                        bearerAuth(token.value)
                        contentType(ContentType.Application.Json)

                        setBody(customerModel)
                    }
                }

            val data = response.body<QuickbooksCustomerDto>()
            data.Customer
        }

    override suspend fun getCustomer(id: String): Result<QuickbooksCustomer> =
        runCatching {
            val response =
                withTokenCheck {
                    client.get("${EssntlConfig.quickbooksBaseUrl}/customer/$id") {
                        bearerAuth(token.value)
                    }
                }

            val data = response.body<QuickbooksCustomerDto>()
            data.Customer
        }

    override suspend fun insertInvoice(invoiceRequest: QuickbooksInvoiceRequest): Result<QuickbooksInvoice> =
        runCatching {
            val response =
                withTokenCheck {
                    client.post("${EssntlConfig.quickbooksBaseUrl}/invoice") {
                        bearerAuth(token.value)
                        contentType(ContentType.Application.Json)

                        setBody(invoiceRequest)
                    }
                }

            val data = response.body<QuickbooksInvoiceDto>()
            data.Invoice
        }

    override suspend fun getInvoice(id: String): Result<QuickbooksInvoice> =
        runCatching {
            val response =
                withTokenCheck {
                    client.get("${EssntlConfig.quickbooksBaseUrl}/invoice/$id") {
                        bearerAuth(token.value)
                    }
                }

            val data = response.body<QuickbooksInvoiceDto>()
            data.Invoice
        }

    override suspend fun shareInvoice(id: String, email: String): Result<Unit> =
        runCatching {
            withTokenCheck {
                client.post("${EssntlConfig.quickbooksBaseUrl}/invoice/$id/send?sendTo=$email") {
                    bearerAuth(token.value)
                }
            }
        }

    override suspend fun downloadInvoicePdf(id: String): Result<String> =
        runCatching {
            val response = withTokenCheck {
                client.get("${EssntlConfig.quickbooksBaseUrl}/invoice/$id/pdf?minorversion=73") {
                    bearerAuth(token.value)
                    contentType(ContentType.Application.Pdf)
                    accept(ContentType.Application.Pdf)
                }
            }

            val filePath =
                if (response.status.isSuccess())
                    saveToTempFile(
                        byteArray = response.readRawBytes(),
                        extension = ".pdf",
                    )
                else
                    null

            filePath ?: throw Exception("Failed to download invoice PDF")
        }


    override suspend fun sparseUpdateInvoice(sparseRequest: QuickbookSparseRequest): Result<QuickbooksInvoice> =
        runCatching {
            val response =
                withTokenCheck {
                    client.post("${EssntlConfig.quickbooksBaseUrl}/invoice") {
                        bearerAuth(token.value)
                        contentType(ContentType.Application.Json)

                        setBody(sparseRequest)
                    }
                }

            val data = response.body<QuickbooksInvoiceDto>()
            data.Invoice
        }

    override suspend fun updateInvoice(updateRequest: QuickbookUpdateRequest): Result<QuickbooksInvoice> =
        runCatching {
            val response =
                withTokenCheck {
                    client.post("${EssntlConfig.quickbooksBaseUrl}/invoice") {
                        bearerAuth(token.value)
                        contentType(ContentType.Application.Json)

                        setBody(updateRequest)
                    }
                }

            val data = response.body<QuickbooksInvoiceDto>()
            data.Invoice
        }

    override suspend fun deleteInvoice(id: String, syncToken: String): Result<Unit> =
        runCatching {
            val response =
                withTokenCheck {
                    client.post("${EssntlConfig.quickbooksBaseUrl}/invoice?operation=delete") {
                        bearerAuth(token.value)

                        setBody(
                            mapOf(
                                "SyncToken" to syncToken,
                                "Id" to id,
                            ),
                        )
                    }
                }

            if (response.status.isSuccess())
                Result.success(Unit)
            else
                Result.failure(Exception("Failed to delete invoice"))
        }

    override suspend fun getItem(id: String): Result<QuickbooksItem> =
        runCatching {
            val response =
                withTokenCheck {
                    client.get("${EssntlConfig.quickbooksBaseUrl}/item/$id") {
                        bearerAuth(token.value)
                    }
                }

            val data = response.body<QuickbooksItemDto>()
            data.Item
        }

    override suspend fun insertItem(itemRequest: QuickbooksItemRequest): Result<QuickbooksItem> =
        runCatching {
            val response =
                withTokenCheck {
                    client.post("${EssntlConfig.quickbooksBaseUrl}/item") {
                        bearerAuth(token.value)
                        contentType(ContentType.Application.Json)

                        setBody(itemRequest)
                    }
                }

            if (response.isDuplicateNameError()) {
                val item = getItemByName(name = itemRequest.Name)
                    ?: throw Exception("Item with name ${itemRequest.Name} already exists")

                return@runCatching item
            }

            val data = response.body<QuickbooksItemDto>()
            data.Item
        }

    override suspend fun getCategory(id: String): Result<QuickbooksCategory> =
        runCatching {
            val response =
                withTokenCheck {
                    client.get("${EssntlConfig.quickbooksBaseUrl}/item/$id?minorversion=4") {
                        bearerAuth(token.value)
                    }
                }

            val data = response.body<QuickbooksCategoryDto>()
            data.Item
        }

    override suspend fun insertCategory(categoryRequest: QuickbooksCategoryRequest): Result<QuickbooksCategory> =
        runCatching {
            val response =
                withTokenCheck {
                    client.post("${EssntlConfig.quickbooksBaseUrl}/item?minorversion=4") {
                        bearerAuth(token.value)
                        contentType(ContentType.Application.Json)

                        setBody(categoryRequest)
                    }
                }

            if (response.isDuplicateNameError()) {
                val item = getCategoryByName(name = categoryRequest.name)
                    ?: throw Exception("Item with name ${categoryRequest.name} already exists")

                return@runCatching item
            }

            val data = response.body<QuickbooksCategoryDto>()
            data.Item
        }

    override suspend fun getLatestInvoiceDocNumber(): Result<String?> =
        runCatching {
            val response =
                withTokenCheck {
                    client.get(urlString = "${EssntlConfig.quickbooksBaseUrl}/query") {
                        url {
                            parameters.append(
                                name = "query",
                                value = "SELECT DocNumber FROM Invoice ORDER BY MetaData.CreateTime DESC maxResults 1",
                            )
                            parameters.append("minorversion", "4")
                            bearerAuth(token.value)
                        }
                    }
                }

            response.body<QuickbooksDocNumberDto>().QueryResponse.Invoice.firstOrNull()?.DocNumber
        }

    private suspend fun getItemByName(name: String): QuickbooksItem? {
        val response =
            withTokenCheck {
                client.get(
                    "${EssntlConfig.quickbooksBaseUrl}/query?query=SELECT * FROM Item WHERE Name = '${
                        name.trim().encodeURLParameter()
                    }' maxresults 1",
                ) {
                    bearerAuth(token.value)
                }
            }

        val data = response.body<QuickbooksItemQueryDto>()
        return data.QueryResponse.Item.firstOrNull()
    }

    private suspend fun getCategoryByName(name: String): QuickbooksCategory? {
        val response =
            withTokenCheck {
                client.get(
                    "${EssntlConfig.quickbooksBaseUrl}/query?query=SELECT * FROM Item WHERE Name = '${
                        name.trim().encodeURLParameter()
                    }' maxresults 1&minorversion=4",
                ) {
                    bearerAuth(token.value)
                }
            }

        val data = response.body<QuickbooksCategoryQueryDto>()
        return data.QueryResponse.Item.firstOrNull()
    }

    private suspend fun getCustomerByEmail(email: String): QuickbooksCustomerExistsDto.QueryResponseDto.CustomerDto? {
        val response =
            withTokenCheck {
                client.get("${EssntlConfig.quickbooksBaseUrl}/query") {
                    url {
                        parameters.append(
                            "query",
                            "SELECT * FROM Customer WHERE PrimaryEmailAddr = '$email' maxresults 1",
                        )
                        parameters.append("minorversion", "4")
                    }
                    bearerAuth(token.value)
                }
            }

        val data = response.body<QuickbooksCustomerExistsDto?>()
        return data?.QueryResponse?.Customer?.firstOrNull()
    }

    companion object {
        // You can find the error codes here:
        // https://developer.intuit.com/app/developer/qbo/docs/develop/troubleshooting/error-codes
        internal const val DuplicateNameErrorCode = 6240
        internal const val BusinessValidationErrorCode = 6000
    }
}
